import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useSearchParams } from "wouter";
import {
  activityLine,
  competencyStanding,
  ctcBand,
  debriefLine,
  DEFAULT_PAGE_SIZE,
  expiryLabel,
  exportRoster,
  getRoster,
  initials,
  onDay,
  openCandidate,
  outcomeLabel,
  pageSummary,
  passState,
  personName,
  readDebrief,
  refusalSentence,
  roundLabel,
  SEGMENT_LABEL,
  sinceLabel,
  sourceLabel,
  stageLabel,
  trackLabel,
  verdictLabel,
} from "../../api/candidates";
import type {
  DebriefBody,
  Dossier,
  Roster,
  RosterQuery,
  RosterRow,
  Segment,
  Totals,
} from "../../api/candidates";
import { actOnPayments, formatPaise, loadPayments, type PlanView } from "../../api/payments";
import { download, stampedName, toCsv, type Cell } from "../../api/csv";
import { Loading } from "../Loading";

/**
 * Full admin access, as the founder chose it: any candidate can be opened and
 * any debrief read verbatim. Opening one writes a row to the audit ledger
 * before a single field is loaded, and the panel says so on screen.
 *
 * The track filter reads whatever `candidate_profile.target_track` holds. There
 * is no ladder and no list of disciplines anywhere in this file.
 */

const SEGMENT_ORDER: readonly Segment[] = ["all", "pass", "expiring", "offers", "dormant"];

const PANEL = "bg-surface-container-lowest border border-outline-variant rounded-xl shadow-sm";
const BUTTON = "px-3 py-2 rounded-lg font-body-sm text-body-sm font-semibold transition-colors disabled:opacity-50";
const PRIMARY = `${BUTTON} bg-primary text-on-primary hover:opacity-90`;
const QUIET = `${BUTTON} bg-surface-container border border-outline-variant text-on-surface hover:bg-surface-container-high`;
const FIELD =
  "w-full bg-surface-container-low border border-outline-variant rounded-lg px-3 py-2 font-body-sm text-body-sm text-on-surface focus:outline-none focus:ring-1 focus:ring-primary";
const LABEL = "font-label-sm text-label-sm uppercase tracking-wider text-on-surface-variant";

function count(value: number): string {
  return value.toLocaleString("en-IN");
}

function Card({
  label,
  value,
  unit,
  foot,
}: {
  readonly label: string;
  readonly value: string;
  readonly unit: string;
  readonly foot: string;
}) {
  return (
    <div className={`${PANEL} p-5 flex flex-col justify-between`}>
      <span className={LABEL}>{label}</span>
      <div className="flex items-baseline gap-2 mt-3">
        <span className="font-headline-md text-headline-md font-bold text-on-surface tracking-tight">
          {value}
        </span>
        <span className="font-body-sm text-body-sm text-on-surface-variant">{unit}</span>
      </div>
      <p className="mt-3 pt-3 border-t border-outline-variant/60 font-label-sm text-label-sm text-on-surface-variant">
        {foot}
      </p>
    </div>
  );
}

function Kpis({ totals }: { readonly totals: Totals }) {
  const passFoot =
    totals.plans === 0
      ? "No plan has been priced yet, so every pass on record was granted by an admin."
      : `${count(totals.expiringPasses)} ${totals.expiringPasses === 1 ? "ends" : "end"} within 14 days, ${count(totals.grantedPasses)} ${totals.grantedPasses === 1 ? "was" : "were"} granted by an admin.`;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
      <Card
        label="Registered candidates"
        value={count(totals.candidates)}
        unit={totals.candidates === 1 ? "account" : "accounts"}
        foot={
          totals.awaitingDeletion === 0
            ? "Nobody is awaiting deletion."
            : `${count(totals.awaitingDeletion)} asked for deletion and are not served here.`
        }
      />
      <Card
        label="In a live search"
        value={count(totals.inSearch)}
        unit={totals.inSearch === 1 ? "candidate" : "candidates"}
        foot={`${count(totals.liveApplications)} applications are open, of any stage short of closed.`}
      />
      <Card
        label="Offers accepted"
        value={count(totals.offersAccepted)}
        unit={totals.offersAccepted === 1 ? "offer" : "offers"}
        foot={`Across ${count(totals.offerCandidates)} ${totals.offerCandidates === 1 ? "candidate" : "candidates"}, counted from closed applications.`}
      />
      <Card
        label="Passes in force"
        value={count(totals.activePasses)}
        unit={totals.activePasses === 1 ? "pass" : "passes"}
        foot={passFoot}
      />
    </div>
  );
}

function Pill({
  active,
  label,
  tally,
  onClick,
}: {
  readonly active: boolean;
  readonly label: string;
  readonly tally: number;
  readonly onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={[
        "px-3 py-1.5 rounded-lg font-body-sm text-body-sm shrink-0 transition-colors border",
        active
          ? "bg-primary text-on-primary border-primary font-semibold"
          : "bg-surface-container text-on-surface-variant border-outline-variant hover:text-on-surface",
      ].join(" ")}
    >
      {label} ({count(tally)})
    </button>
  );
}

function Avatar({ row, dark }: { readonly row: RosterRow; readonly dark: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={[
        "w-9 h-9 rounded-lg flex items-center justify-center shrink-0 font-label-md text-label-md font-bold",
        dark ? "bg-secondary text-on-secondary" : "bg-primary text-on-primary",
      ].join(" ")}
    >
      {initials(row)}
    </span>
  );
}

function RosterTable({
  view,
  now,
  selected,
  busy,
  onOpen,
  onExtend,
  onPage,
  onSize,
}: {
  readonly view: Roster;
  readonly now: number;
  readonly selected: string | null;
  readonly busy: boolean;
  readonly onOpen: (userId: string) => void;
  readonly onExtend: (row: RosterRow) => void;
  readonly onPage: (page: number) => void;
  readonly onSize: (size: number) => void;
}) {
  return (
    <div className={`${PANEL} overflow-hidden flex flex-col`}>
      <div className="px-5 py-3.5 bg-surface-container-low border-b border-outline-variant flex items-center justify-between gap-space-sm">
        <h2 className="font-headline-sm text-headline-sm font-bold text-on-surface">
          Registered candidates
        </h2>
        <span className="font-label-sm text-label-sm px-2 py-0.5 rounded bg-surface-container-high text-on-surface-variant">
          {pageSummary({
            page: view.page,
            pages: view.pages,
            total: view.total,
            shown: view.rows.length,
          })}
        </span>
      </div>

      {view.rows.length === 0 ? (
        <p className="p-8 text-center font-body-sm text-body-sm text-on-surface-variant">
          Nobody matches this filter. The counts on the pills above are live, so a zero there is a
          real zero.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[56rem] text-left border-collapse">
            <thead>
              <tr className="border-b border-outline-variant bg-surface-container-low font-label-sm text-label-sm uppercase tracking-wider text-on-surface-variant">
                <th scope="col" className="py-3 px-4 font-semibold">
                  Candidate
                </th>
                <th scope="col" className="py-3 px-4 font-semibold">
                  Target track and companies
                </th>
                <th scope="col" className="py-3 px-4 font-semibold">
                  Pass
                </th>
                <th scope="col" className="py-3 px-4 font-semibold">
                  Expiry
                </th>
                <th scope="col" className="py-3 px-4 font-semibold">
                  What they have logged
                </th>
                {/* PINNED TO THE RIGHT EDGE. At 1280 the table is wider than its
                    column, and the one action a row has was scrolled 79px out of
                    sight, 27px of a 106px button showing. */}
                <th
                  scope="col"
                  className="py-3 px-4 font-semibold text-right sticky right-0 bg-surface-container-low shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.15)]"
                >
                  Record
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/50">
              {view.rows.map((row) => {
                const pass = passState(row.pass, now);
                const chosen = row.userId === selected;
                const band = ctcBand(row.ctcTargetMinInr, row.ctcTargetMaxInr);
                return (
                  <tr
                    key={row.userId}
                    className={chosen ? "bg-secondary-container/30" : "hover:bg-surface-container-low"}
                  >
                    <td className="py-3.5 px-4 align-top">
                      <div className="flex items-start gap-3 min-w-0">
                        <Avatar row={row} dark={chosen} />
                        <span className="flex flex-col min-w-0">
                          <span className="font-body-sm text-body-sm font-bold text-on-surface truncate">
                            {personName(row)}
                          </span>
                          <span className="font-body-sm text-body-sm text-on-surface-variant truncate">
                            {row.email}
                          </span>
                          <span className="font-label-sm text-label-sm text-on-surface-variant">
                            Signed up {onDay(row.createdAt)}, last seen{" "}
                            {sinceLabel(row.lastSeenAt, now)}
                          </span>
                        </span>
                      </div>
                    </td>
                    <td className="py-3.5 px-4 align-top">
                      <span className="flex flex-col min-w-0">
                        <span className="font-body-sm text-body-sm font-semibold text-on-surface">
                          {row.targetTrack ?? "No track set"}
                        </span>
                        <span className="font-label-sm text-label-sm text-on-surface-variant">
                          {row.companies.length === 0
                            ? "No live application"
                            : row.companies.join(", ")}
                        </span>
                        {band === null ? null : (
                          <span className="font-label-sm text-label-sm text-on-surface-variant">
                            Target {band}
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 align-top">
                      <span
                        className={[
                          "inline-flex items-center px-2.5 py-0.5 rounded-full font-label-sm text-label-sm font-semibold border",
                          pass.tone === "live"
                            ? "bg-secondary-container text-on-secondary-container border-secondary/30"
                            : pass.tone === "warning"
                              ? "bg-surface-container-high text-on-surface border-outline"
                              : "bg-surface-container text-on-surface-variant border-outline-variant",
                        ].join(" ")}
                      >
                        {pass.text}
                      </span>
                      {pass.note === null ? null : (
                        <span className="block mt-1 font-label-sm text-label-sm text-on-surface-variant">
                          {pass.note}
                        </span>
                      )}
                    </td>
                    <td className="py-3.5 px-4 align-top font-label-md text-label-md text-on-surface-variant">
                      {expiryLabel(row.pass, now)}
                    </td>
                    <td className="py-3.5 px-4 align-top font-body-sm text-body-sm text-on-surface-variant max-w-[18rem]">
                      {activityLine(row.counts)}
                    </td>
                    <td className="py-3.5 px-4 align-top text-right sticky right-0 bg-surface-container-lowest shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.15)]">
                      <span className="inline-flex flex-col items-end gap-1.5">
                        <button
                          type="button"
                          onClick={() => onOpen(row.userId)}
                          className="px-3 py-1.5 rounded-lg bg-primary text-on-primary font-label-md text-label-md font-semibold hover:opacity-90 transition-opacity whitespace-nowrap"
                        >
                          {chosen ? "Open again" : "Open record"}
                        </button>
                        {row.pass?.subscriptionId == null ? null : (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => onExtend(row)}
                            className="px-3 py-1.5 rounded-lg border border-outline-variant bg-surface-container-lowest font-label-md text-label-md text-on-surface disabled:opacity-40 whitespace-nowrap"
                          >
                            Extend pass
                          </button>
                        )}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="px-5 py-3 bg-surface-container-low border-t border-outline-variant flex flex-wrap items-center justify-between gap-3 font-label-md text-label-md text-on-surface-variant">
        <label className="flex items-center gap-2">
          <span>Rows a page</span>
          <select
            value={view.pageSize}
            onChange={(event) => onSize(Number(event.target.value))}
            className="bg-surface-container-lowest border border-outline-variant rounded px-2 py-1 text-on-surface"
          >
            {view.pageSizes.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
        <span>
          Page {view.page} of {view.pages}
        </span>
        <span className="inline-flex gap-2">
          <button
            type="button"
            disabled={view.page <= 1}
            onClick={() => onPage(view.page - 1)}
            className="px-2.5 py-1 rounded border border-outline-variant bg-surface-container-lowest disabled:opacity-40"
          >
            Previous
          </button>
          <button
            type="button"
            disabled={view.page >= view.pages}
            onClick={() => onPage(view.page + 1)}
            className="px-2.5 py-1 rounded border border-outline-variant bg-surface-container-lowest disabled:opacity-40"
          >
            Next
          </button>
        </span>
      </div>
    </div>
  );
}

function Section({
  title,
  note,
  children,
}: {
  readonly title: string;
  readonly note?: string | undefined;
  readonly children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="font-label-sm text-label-sm uppercase tracking-wider font-semibold text-on-surface-variant">
        {title}
      </h3>
      {note === undefined ? null : (
        <p className="font-body-sm text-body-sm text-on-surface-variant">{note}</p>
      )}
      {children}
    </section>
  );
}

function DossierPanel({
  dossier,
  now,
  bodies,
  busy,
  onReadDebrief,
  onExtend,
  onCancel,
  onDownload,
  reading,
}: {
  readonly dossier: Dossier;
  readonly now: number;
  readonly bodies: Readonly<Record<string, DebriefBody>>;
  readonly busy: boolean;
  readonly onReadDebrief: (debriefId: string) => void;
  readonly onExtend: (subscriptionId: string) => void;
  readonly onCancel: (subscriptionId: string) => void;
  readonly onDownload: () => void;
  readonly reading: string | null;
}) {
  const pass = passState(dossier.pass, now);
  const band = ctcBand(
    dossier.profile?.ctcTargetMinInr ?? null,
    dossier.profile?.ctcTargetMaxInr ?? null,
  );
  const live = dossier.pass;
  return (
    <div className={`${PANEL} overflow-hidden flex flex-col`}>
      <div className="p-5 bg-primary text-on-primary flex flex-col gap-3">
        <span className="font-label-sm text-label-sm uppercase tracking-wider font-bold text-secondary-fixed-dim">
          Candidate record
        </span>
        <div className="flex items-start gap-3 min-w-0">
          <span
            aria-hidden="true"
            className="w-12 h-12 rounded-xl bg-primary-container border border-outline/40 text-secondary-fixed-dim flex items-center justify-center font-label-md text-label-md font-bold shrink-0"
          >
            {initials(dossier.identity)}
          </span>
          <span className="flex flex-col min-w-0">
            <h2 className="font-headline-sm text-headline-sm font-bold truncate">
              {personName(dossier.identity)}
            </h2>
            <span className="font-body-sm text-body-sm text-inverse-on-surface/80 truncate">
              {dossier.identity.email}
            </span>
            <span className="font-label-sm text-label-sm text-inverse-on-surface/70 mt-1">
              Signed up {onDay(dossier.identity.createdAt)}, last seen{" "}
              {sinceLabel(dossier.identity.lastSeenAt, now)}
            </span>
          </span>
        </div>
        <p className="font-label-sm text-label-sm text-inverse-on-surface/80 border-t border-outline/40 pt-3">
          This open was written to the audit ledger at {new Date(dossier.audit.at).toISOString()} as{" "}
          {dossier.audit.action}. Row {dossier.audit.auditId}.{" "}
          <a href="/audit" className="underline">
            See the ledger
          </a>
        </p>
      </div>

      <div className="p-5 flex flex-col gap-5">
        <Section title="Pass">
          <p className="font-body-sm text-body-sm text-on-surface">{pass.text}</p>
          {dossier.passes.length === 0 ? (
            <p className="font-label-sm text-label-sm text-on-surface-variant">
              No subscription row has ever been written for this account.
            </p>
          ) : (
            <ul className="flex flex-col gap-1">
              {dossier.passes.map((one) => (
                <li
                  key={`${one.startedAt}-${one.expiresAt}`}
                  className="font-label-sm text-label-sm text-on-surface-variant"
                >
                  {one.planName ?? "Pass"}, {one.status}, {onDay(one.startedAt)} to{" "}
                  {onDay(one.expiresAt)}
                  {one.grantedBy === null ? "" : ", granted by an admin"}
                </li>
              ))}
            </ul>
          )}
          {live?.subscriptionId == null ? null : (
            <div className="flex flex-wrap gap-2 pt-1">
              <button
                type="button"
                className={QUIET}
                disabled={busy}
                onClick={() => onExtend(live.subscriptionId ?? "")}
              >
                Extend this pass
              </button>
              <button
                type="button"
                className={QUIET}
                disabled={busy || live.status === "cancelled"}
                onClick={() => onCancel(live.subscriptionId ?? "")}
              >
                Cancel this pass
              </button>
            </div>
          )}
        </Section>

        <Section title="What they are aiming at">
          {dossier.profile === null ? (
            <p className="font-body-sm text-body-sm text-on-surface-variant">
              No profile saved. Nothing has been filled in.
            </p>
          ) : (
            <ul className="flex flex-col gap-1 font-body-sm text-body-sm text-on-surface-variant">
              <li>Track: {dossier.profile.targetTrack ?? "not set"}</li>
              <li>
                Experience:{" "}
                {dossier.profile.yearsExperience === null
                  ? "not set"
                  : `${dossier.profile.yearsExperience} years`}
              </li>
              <li>Target pay: {band ?? "not set"}</li>
              <li>Mock difficulty: {dossier.profile.mockDifficulty}</li>
              <li>
                Domains: {dossier.domains.length === 0 ? "none chosen" : dossier.domains.join(", ")}
              </li>
            </ul>
          )}
        </Section>

        <Section title={`Applications (${count(dossier.applications.length)})`}>
          {dossier.applications.length === 0 ? (
            <p className="font-body-sm text-body-sm text-on-surface-variant">None logged.</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {dossier.applications.map((one) => (
                <li
                  key={one.applicationId}
                  className="border border-outline-variant rounded-lg p-3 bg-surface-container-low"
                >
                  <span className="font-body-sm text-body-sm font-semibold text-on-surface block">
                    {one.company}, {one.roleTitle}
                  </span>
                  <span className="font-label-sm text-label-sm text-on-surface-variant block mt-0.5">
                    {stageLabel(one.stage)}
                    {one.outcome === null ? "" : `, ${outcomeLabel(one.outcome)}`}, via{" "}
                    {sourceLabel(one.source)}, applied {onDay(one.appliedAt)}
                  </span>
                  {ctcBand(one.ctcMinInr, one.ctcMaxInr) === null ? null : (
                    <span className="font-label-sm text-label-sm text-on-surface-variant block">
                      Band {ctcBand(one.ctcMinInr, one.ctcMaxInr)}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title={`Rounds (${count(dossier.rounds.length)})`}>
          {dossier.rounds.length === 0 ? (
            <p className="font-body-sm text-body-sm text-on-surface-variant">None logged.</p>
          ) : (
            <ul className="flex flex-col gap-1 font-body-sm text-body-sm text-on-surface-variant">
              {dossier.rounds.map((one) => (
                <li key={one.roundId}>
                  {one.company ?? "Unknown company"}, round {one.sequence}, {roundLabel(one.kind)},{" "}
                  {verdictLabel(one.verdict)}
                  {one.happenedAt === null ? "" : `, sat ${onDay(one.happenedAt)}`}
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section
          title={`Debriefs (${count(dossier.debriefs.length)})`}
          note="Reading one in full serves the text exactly as it was written, and writes a second ledger row naming that debrief."
        >
          {dossier.debriefs.length === 0 ? (
            <p className="font-body-sm text-body-sm text-on-surface-variant">None written.</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {dossier.debriefs.map((one) => {
                const shown = bodies[one.debriefId];
                return (
                  <li
                    key={one.debriefId}
                    className="border border-outline-variant rounded-lg p-3 bg-surface-container-low"
                  >
                    <span className="font-body-sm text-body-sm font-semibold text-on-surface block">
                      {onDay(one.writtenAt)}
                    </span>
                    <span className="font-label-sm text-label-sm text-on-surface-variant block mt-0.5">
                      {debriefLine(one)}
                    </span>
                    {shown === undefined ? (
                      <button
                        type="button"
                        disabled={reading === one.debriefId}
                        onClick={() => onReadDebrief(one.debriefId)}
                        className="mt-2 px-3 py-1.5 rounded-lg border border-outline bg-surface-container-lowest font-label-md text-label-md font-semibold text-on-surface disabled:opacity-40"
                      >
                        {reading === one.debriefId ? "Opening" : "Read in full"}
                      </button>
                    ) : (
                      <>
                        <pre className="mt-2 whitespace-pre-wrap break-words font-body-sm text-body-sm text-on-surface bg-surface-container-lowest border border-outline-variant rounded p-3">
                          {shown.body}
                        </pre>
                        <span className="font-label-sm text-label-sm text-on-surface-variant block mt-1">
                          Recorded as {shown.audit.action}, row {shown.audit.auditId}
                        </span>
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </Section>

        <Section title={`Mock sessions (${count(dossier.mocks.length)})`}>
          {dossier.mocks.length === 0 ? (
            <p className="font-body-sm text-body-sm text-on-surface-variant">None run.</p>
          ) : (
            <ul className="flex flex-col gap-1 font-body-sm text-body-sm text-on-surface-variant">
              {dossier.mocks.map((one) => (
                <li key={one.sessionId}>
                  {onDay(one.startedAt)}, {roundLabel(one.roundKind)}, {one.difficulty},{" "}
                  {count(one.turns)} turns
                  {one.endedAt === null ? ", still open" : ""}
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section
          title={`Competencies named (${count(dossier.competencies.length)})`}
          note="Counts of rounds that named a competency, grouped by whatever family the skill table gives them. Nothing here is a score."
        >
          {dossier.competencies.length === 0 ? (
            <p className="font-body-sm text-body-sm text-on-surface-variant">
              Nothing named yet. The ledger fills from debriefs the model has read.
            </p>
          ) : (
            <ul className="flex flex-col gap-1">
              {dossier.competencies.map((one) => (
                <li key={one.skillId} className="font-body-sm text-body-sm text-on-surface-variant">
                  <span className="text-on-surface font-semibold">{one.label}</span>
                  {one.family === null ? "" : ` (${one.family.replace(/_/g, " ")})`}
                  {": "}
                  {competencyStanding(one)}
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section
          title="This record as a file"
          note="Everything above, already served to you and already in the ledger. Downloading it adds no row because it exposes nothing new."
        >
          <button type="button" className={QUIET} onClick={onDownload}>
            Download this record (CSV)
          </button>
        </Section>
      </div>
    </div>
  );
}

function Dialog({
  title,
  warning,
  busy,
  confirmLabel,
  onClose,
  onConfirm,
  children,
}: {
  readonly title: string;
  readonly warning: string;
  readonly busy: boolean;
  readonly confirmLabel: string;
  readonly onClose: () => void;
  readonly onConfirm: () => void;
  readonly children: ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 z-[60] bg-inverse-surface/60 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onKeyDown={(event) => {
        if (event.key === "Escape") onClose();
      }}
    >
      <div className={`${PANEL} w-full max-w-lg p-6 space-y-4`}>
        <h3 className="font-headline-sm text-headline-sm font-bold text-on-surface">{title}</h3>
        <p className="font-body-sm text-body-sm text-on-surface-variant">{warning}</p>
        {children}
        <div className="flex items-center justify-end gap-2">
          <button type="button" className={QUIET} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className={PRIMARY} disabled={busy} onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

type PassTarget = { readonly subscriptionId: string; readonly who: string };

export function CandidateDeckScreen() {
  const [params, setParams] = useSearchParams();
  const fromUrl = params.get("q") ?? "";

  const [segment, setSegment] = useState<Segment>("all");
  const [typed, setTyped] = useState(fromUrl);
  const [query, setQuery] = useState(fromUrl);
  const [track, setTrack] = useState("");
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(DEFAULT_PAGE_SIZE);

  const [roster, setRoster] = useState<Roster | null>(null);
  const [rosterError, setRosterError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [stamp, setStamp] = useState(0);

  const [selected, setSelected] = useState<string | null>(null);
  const [dossier, setDossier] = useState<Dossier | null>(null);
  const [dossierError, setDossierError] = useState<string | null>(null);
  const [opening, setOpening] = useState(false);

  const [bodies, setBodies] = useState<Record<string, DebriefBody>>({});
  const [reading, setReading] = useState<string | null>(null);

  const [extending, setExtending] = useState<PassTarget | null>(null);
  const [cancelling, setCancelling] = useState<PassTarget | null>(null);
  const [granting, setGranting] = useState(false);
  const [bulk, setBulk] = useState(false);

  const now = Date.now();

  useEffect(() => {
    if (fromUrl.length === 0) return;
    setTyped(fromUrl);
    setQuery(fromUrl);
    setPage(1);
    setParams(new URLSearchParams(), { replace: true });
  }, [fromUrl, setParams]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery(typed);
      setPage(1);
    }, 250);
    return () => clearTimeout(timer);
  }, [typed]);

  const where: RosterQuery = useMemo(
    () => ({ segment, query, track, page, size }),
    [segment, query, track, page, size],
  );

  useEffect(() => {
    let live = true;
    getRoster(where)
      .then((view) => {
        if (!live) return;
        setRoster(view);
        setRosterError(null);
      })
      .catch((caught: unknown) => {
        if (!live) return;
        setRosterError(caught instanceof Error ? caught.message : "failed");
      });
    return () => {
      live = false;
    };
  }, [where, stamp]);

  const reload = useCallback(() => setStamp((one) => one + 1), []);

  const open = useCallback((userId: string) => {
    setSelected(userId);
    setOpening(true);
    setDossierError(null);
    setBodies({});
    openCandidate(userId)
      .then((found) => {
        setDossier(found);
        setOpening(false);
      })
      .catch((caught: unknown) => {
        setDossier(null);
        setOpening(false);
        setDossierError(caught instanceof Error ? caught.message : "failed");
      });
  }, []);

  const read = useCallback(
    (debriefId: string) => {
      if (selected === null) return;
      setReading(debriefId);
      readDebrief(selected, debriefId)
        .then((found) => {
          setBodies((held) => ({ ...held, [debriefId]: found }));
          setReading(null);
        })
        .catch(() => {
          setReading(null);
        });
    },
    [selected],
  );

  const run = useCallback(
    async (work: () => Promise<string>) => {
      setBusy(true);
      setNotice(null);
      try {
        const said = await work();
        setNotice(said);
        setRosterError(null);
        reload();
        if (selected !== null) open(selected);
      } catch (caught) {
        setRosterError(caught instanceof Error ? caught.message : "failed");
      } finally {
        setBusy(false);
      }
    },
    [open, reload, selected],
  );

  const exportAll = useCallback(() => {
    void run(async () => {
      const answer = await exportRoster(where);
      download(
        stampedName("prephike-roster", answer.exportedAt),
        toCsv(
          [
            "email",
            "name",
            "signed up",
            "last seen",
            "target track",
            "years experience",
            "pass",
            "pass status",
            "pass expires",
            "applications",
            "live applications",
            "offers",
            "rounds",
            "debriefs",
            "mocks",
            "competencies",
          ],
          answer.rows.map((row): readonly Cell[] => [
            row.email,
            row.displayName,
            onDay(row.createdAt),
            onDay(row.lastSeenAt),
            row.targetTrack,
            row.yearsExperience,
            row.pass?.planName ?? null,
            row.pass?.status ?? null,
            row.pass === null ? null : onDay(row.pass.expiresAt),
            row.counts.applications,
            row.counts.liveApplications,
            row.counts.offers,
            row.counts.rounds,
            row.counts.debriefs,
            row.counts.mocks,
            row.counts.competencies,
          ]),
        ),
      );
      const capped =
        answer.matching > answer.cap
          ? ` The filter matches ${count(answer.matching)}, and an export is capped at ${count(answer.cap)}.`
          : "";
      return `Exported ${count(answer.rows.length)} candidates, and wrote one ledger row for each of them.${capped}`;
    });
  }, [run, where]);

  const downloadDossier = useCallback(() => {
    if (dossier === null) return;
    const rows: (readonly Cell[])[] = [
      ["identity", "email", dossier.identity.email],
      ["identity", "name", dossier.identity.displayName],
      ["identity", "signed up", onDay(dossier.identity.createdAt)],
      ["identity", "last seen", onDay(dossier.identity.lastSeenAt)],
      ["profile", "track", dossier.profile?.targetTrack ?? null],
      ["profile", "years", dossier.profile?.yearsExperience ?? null],
      ["profile", "domains", dossier.domains.join(", ")],
      ["ledger", "this open", dossier.audit.auditId],
    ];
    for (const one of dossier.applications) {
      rows.push(["application", `${one.company}, ${one.roleTitle}`, `${stageLabel(one.stage)}${one.outcome === null ? "" : `, ${outcomeLabel(one.outcome)}`}`]);
    }
    for (const one of dossier.rounds) {
      rows.push(["round", `${one.company ?? "unknown"} ${roundLabel(one.kind)}`, verdictLabel(one.verdict)]);
    }
    for (const one of dossier.debriefs) {
      rows.push(["debrief", onDay(one.writtenAt), debriefLine(one)]);
    }
    for (const one of dossier.mocks) {
      rows.push(["mock", onDay(one.startedAt), `${roundLabel(one.roundKind)}, ${one.turns} turns`]);
    }
    for (const one of dossier.competencies) {
      rows.push(["competency", one.label, competencyStanding(one)]);
    }
    download(
      stampedName(`prephike-record-${dossier.identity.userId.slice(0, 8)}`, dossier.audit.at),
      toCsv(["what", "which", "detail"], rows),
    );
  }, [dossier]);

  const withPass = useMemo(
    () => (roster?.rows ?? []).filter((row) => row.pass?.subscriptionId != null),
    [roster],
  );

  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col gap-space-lg">
      <header className={`${PANEL} p-6 flex flex-col xl:flex-row xl:items-end justify-between gap-4`}>
        <div className="flex flex-col gap-2 max-w-3xl">
          <span className="font-label-sm text-label-sm uppercase tracking-wider font-semibold text-secondary">
            Candidate lifecycle and identity
          </span>
          <h1 className="font-headline-lg text-headline-lg text-on-surface font-bold tracking-tight">
            Candidate and User Control Deck
          </h1>
          <p className="font-body-md text-body-md text-on-surface-variant">
            Every account, with what it actually holds. An admin may open any record and read any
            debrief exactly as the candidate wrote it. Each of those two acts writes a row to the
            audit ledger naming who did it, to whom, and when.
          </p>
        </div>
        {/* NOT BEFORE THE ROSTER LANDS. "Extend every pass on this page" is a
            bulk write, and at first paint the page it refers to is empty. */}
        <div className={roster === null ? "hidden" : "flex flex-wrap items-center gap-2"}>
          <button type="button" className={PRIMARY} disabled={busy} onClick={() => setGranting(true)}>
            Grant a pass
          </button>
          <button
            type="button"
            className={QUIET}
            disabled={busy || withPass.length === 0}
            onClick={() => setBulk(true)}
          >
            Extend every pass on this page
          </button>
          <button type="button" className={QUIET} disabled={busy} onClick={exportAll}>
            Export the roster (CSV)
          </button>
        </div>
      </header>

      {rosterError !== null ? (
        <p className="bg-error-container text-on-error-container border border-error/30 rounded-xl p-4 font-body-sm text-body-sm">
          {refusalSentence(rosterError)}
        </p>
      ) : null}

      {notice !== null ? (
        <p
          role="status"
          aria-live="polite"
          className="bg-secondary-container text-on-secondary-container border border-secondary/40 rounded-xl p-4 font-body-sm text-body-sm flex items-start justify-between gap-4"
        >
          <span>{notice}</span>
          <button type="button" className="underline shrink-0" onClick={() => setNotice(null)}>
            Dismiss
          </button>
        </p>
      ) : null}

      {roster === null ? (
        <Loading what="Reading the roster." />
      ) : (
        <>
          <Kpis totals={roster.totals} />

          <div className={`${PANEL} p-4 flex flex-col gap-4`}>
            <div className="flex flex-col lg:flex-row lg:items-center gap-4">
              <label className="flex-1 min-w-0">
                <span className="sr-only">Search candidates</span>
                <input
                  type="search"
                  value={typed}
                  onChange={(event) => setTyped(event.target.value)}
                  placeholder="Search by name, address or target track"
                  className="w-full h-10 px-3 bg-surface-container-low border border-outline-variant rounded-lg font-body-sm text-body-sm text-on-surface placeholder:text-on-surface-variant focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </label>
              {/* THE TRACK FILTER, ONLY ONCE IT SEPARATES ANYBODY. Every
                  candidate sat in "No track saved", so the control partitioned
                  nothing and the note under it was longer than the control. */}
              {roster.tracks.filter((one) => one.track !== "").length > 0 ? (
                <label className="shrink-0 flex items-center gap-2 font-label-sm text-label-sm text-on-surface-variant">
                  <span>Track</span>
                  <select
                    value={track}
                    onChange={(event) => {
                      setTrack(event.target.value);
                      setPage(1);
                    }}
                    className="h-10 px-2 bg-surface-container-low border border-outline-variant rounded-lg font-body-sm text-body-sm text-on-surface max-w-[16rem]"
                  >
                    <option value="">Every track ({count(roster.totals.candidates)})</option>
                    {roster.tracks.map((one) => (
                      <option key={one.track} value={one.track}>
                        {trackLabel(one.track)} ({count(one.count)})
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
            </div>
            <div className="flex items-center gap-2 overflow-x-auto">
              {SEGMENT_ORDER.map((key) => (
                <Pill
                  key={key}
                  active={segment === key}
                  label={SEGMENT_LABEL[key]}
                  tally={roster.segments.find((one) => one.key === key)?.count ?? 0}
                  onClick={() => {
                    setSegment(key);
                    setPage(1);
                  }}
                />
              ))}
            </div>
            <p className="font-label-sm text-label-sm text-on-surface-variant">
              The track list is every distinct value the profile table holds, counted. Nothing here
              knows a seniority ladder or a discipline: a track is whatever the candidate typed.
            </p>
          </div>

          {roster.totals.candidates === 0 ? (
            <div className={`${PANEL} p-8 text-center`}>
              <h2 className="font-headline-sm text-headline-sm font-bold text-on-surface">
                No accounts yet
              </h2>
              <p className="font-body-sm text-body-sm text-on-surface-variant mt-1">
                Nobody has signed up. When somebody does, their record appears here with their own
                counts and nothing estimated.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-start">
              <div className="xl:col-span-8 min-w-0">
                <RosterTable
                  view={roster}
                  now={now}
                  selected={selected}
                  busy={busy}
                  onOpen={open}
                  onExtend={(row) =>
                    setExtending({
                      subscriptionId: row.pass?.subscriptionId ?? "",
                      who: personName(row),
                    })
                  }
                  onPage={setPage}
                  onSize={(next) => {
                    setSize(next);
                    setPage(1);
                  }}
                />
              </div>
              <aside className="xl:col-span-4 min-w-0 xl:sticky xl:top-24">
                {dossierError !== null ? (
                  <p className="bg-error-container text-on-error-container border border-error/30 rounded-xl p-4 font-body-sm text-body-sm">
                    {refusalSentence(dossierError)}
                  </p>
                ) : opening ? (
                  <p className={`${PANEL} p-6 font-body-sm text-body-sm text-on-surface-variant`}>
                    Writing the ledger row, then loading the record.
                  </p>
                ) : dossier === null ? (
                  <div className={`${PANEL} p-6`}>
                    <h2 className="font-headline-sm text-headline-sm font-bold text-on-surface">
                      No record open
                    </h2>
                    <p className="font-body-sm text-body-sm text-on-surface-variant mt-1">
                      Open one from the table. Opening is a privileged read: it writes a row naming
                      you, the candidate and the instant, before any of their data is loaded.
                    </p>
                  </div>
                ) : (
                  <DossierPanel
                    dossier={dossier}
                    now={now}
                    bodies={bodies}
                    busy={busy}
                    onReadDebrief={read}
                    onExtend={(subscriptionId) =>
                      setExtending({
                        subscriptionId,
                        who: personName(dossier.identity),
                      })
                    }
                    onCancel={(subscriptionId) =>
                      setCancelling({
                        subscriptionId,
                        who: personName(dossier.identity),
                      })
                    }
                    onDownload={downloadDossier}
                    reading={reading}
                  />
                )}
              </aside>
            </div>
          )}


          <p className="font-label-sm text-label-sm text-on-surface-variant">
            Pay is shown in lakhs of rupees, converted from the whole rupees the database holds.
            {roster.totals.plans === 0
              ? " No plan has been priced, so no pass here was bought."
              : ` ${count(roster.totals.plans)} plans are priced.`}{" "}
            Debriefs written: {count(roster.totals.debriefs)}, of which{" "}
            {count(roster.totals.debriefsRead)} have been read by the model. An export is capped at{" "}
            {count(roster.exportCap)} rows.
          </p>
        </>
      )}

      {extending !== null && (
        <ExtendDialog
          target={extending}
          busy={busy}
          onClose={() => setExtending(null)}
          onConfirm={(days) => {
            const target = extending;
            setExtending(null);
            void run(async () => {
              await actOnPayments({
                action: "extend_pass",
                subscriptionId: target.subscriptionId,
                days,
              });
              return `Extended ${target.who}'s pass by ${days} days. An audit row was written.`;
            });
          }}
        />
      )}

      {cancelling !== null && (
        <CancelDialog
          target={cancelling}
          busy={busy}
          onClose={() => setCancelling(null)}
          onConfirm={(reason) => {
            const target = cancelling;
            setCancelling(null);
            void run(async () => {
              await actOnPayments({
                action: "cancel_pass",
                subscriptionId: target.subscriptionId,
                reason,
              });
              return `Cancelled ${target.who}'s pass. No money moved: a refund is a separate act on Payments.`;
            });
          }}
        />
      )}

      {granting && (
        <GrantDialog
          busy={busy}
          onClose={() => setGranting(false)}
          onConfirm={(email, planId, days) => {
            setGranting(false);
            void run(async () => {
              await actOnPayments({ action: "grant_pass", email, planId, days });
              return `Granted a pass to ${email} for ${days} days. An audit row was written.`;
            });
          }}
        />
      )}

      {bulk && (
        <BulkDialog
          howMany={withPass.length}
          busy={busy}
          onClose={() => setBulk(false)}
          onConfirm={(days) => {
            setBulk(false);
            void run(async () => {
              for (const row of withPass) {
                await actOnPayments({
                  action: "extend_pass",
                  subscriptionId: row.pass?.subscriptionId ?? "",
                  days,
                });
              }
              return `Extended ${count(withPass.length)} passes by ${days} days, and wrote one audit row for each.`;
            });
          }}
        />
      )}
    </div>
  );
}

function wholeDays(value: string): number | null {
  if (!/^\d{1,4}$/.test(value.trim())) return null;
  const days = Number(value.trim());
  return days >= 1 ? days : null;
}

function ExtendDialog({
  target,
  busy,
  onClose,
  onConfirm,
}: {
  readonly target: PassTarget;
  readonly busy: boolean;
  readonly onClose: () => void;
  readonly onConfirm: (days: number) => void;
}) {
  const [days, setDays] = useState("30");
  const parsed = wholeDays(days);
  return (
    <Dialog
      title={`Extend the pass held by ${target.who}`}
      warning="The days are added to the later of today and the current expiry, so extending a lapsed pass gives the whole span rather than a date in the past. This writes a row to the audit ledger naming this candidate."
      busy={busy || parsed === null}
      confirmLabel="Extend the pass"
      onClose={onClose}
      onConfirm={() => {
        if (parsed !== null) onConfirm(parsed);
      }}
    >
      <label className="block space-y-1">
        <span className={`${LABEL} block`}>Days</span>
        <input
          className={FIELD}
          value={days}
          inputMode="numeric"
          onChange={(event) => setDays(event.target.value)}
        />
        {parsed === null && (
          <span className="block font-body-sm text-body-sm text-error">
            A whole number of days, one or more.
          </span>
        )}
      </label>
    </Dialog>
  );
}

function CancelDialog({
  target,
  busy,
  onClose,
  onConfirm,
}: {
  readonly target: PassTarget;
  readonly busy: boolean;
  readonly onClose: () => void;
  readonly onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState("");
  return (
    <Dialog
      title={`Cancel the pass held by ${target.who}`}
      warning="This ends the pass now. No money moves and nothing is returned: a refund is a separate act, on Payments, against the payment row. The reason is stored on the audit row."
      busy={busy}
      confirmLabel="Cancel the pass"
      onClose={onClose}
      onConfirm={() => onConfirm(reason.trim())}
    >
      <label className="block space-y-1">
        <span className={`${LABEL} block`}>Reason</span>
        <input
          className={FIELD}
          value={reason}
          maxLength={200}
          onChange={(event) => setReason(event.target.value)}
        />
      </label>
    </Dialog>
  );
}

function GrantDialog({
  busy,
  onClose,
  onConfirm,
}: {
  readonly busy: boolean;
  readonly onClose: () => void;
  readonly onConfirm: (email: string, planId: string, days: number) => void;
}) {
  const [plans, setPlans] = useState<readonly PlanView[] | null>(null);
  const [email, setEmail] = useState("");
  const [planId, setPlanId] = useState("");
  const [days, setDays] = useState("90");
  const parsed = wholeDays(days);

  useEffect(() => {
    let live = true;
    loadPayments()
      .then((payload) => {
        if (live) setPlans(payload.plans.filter((one) => one.active));
      })
      .catch(() => {
        if (live) setPlans([]);
      });
    return () => {
      live = false;
    };
  }, []);

  return (
    <Dialog
      title="Grant a pass"
      warning="A pass an admin gave rather than one somebody bought. It charges nothing and it writes a row to the audit ledger naming this candidate. No tier is suggested here: the list is the plans that exist."
      busy={busy || parsed === null || planId.length === 0 || email.length === 0}
      confirmLabel="Grant the pass"
      onClose={onClose}
      onConfirm={() => {
        if (parsed !== null) onConfirm(email.trim(), planId, parsed);
      }}
    >
      <div className="space-y-3">
        <label className="block space-y-1">
          <span className={`${LABEL} block`}>Candidate email</span>
          <input
            className={FIELD}
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>
        <label className="block space-y-1">
          <span className={`${LABEL} block`}>Plan</span>
          <select
            className={FIELD}
            value={planId}
            onChange={(event) => setPlanId(event.target.value)}
          >
            <option value="">Choose a plan</option>
            {(plans ?? []).map((plan) => (
              <option key={plan.planId} value={plan.planId}>
                {plan.name}, {formatPaise(plan.amountPaise)}
              </option>
            ))}
          </select>
          {plans !== null && plans.length === 0 && (
            <span className="block font-body-sm text-body-sm text-on-surface-variant">
              No plan has been priced. Create one on Payments first.
            </span>
          )}
        </label>
        <label className="block space-y-1">
          <span className={`${LABEL} block`}>Days</span>
          <input
            className={FIELD}
            value={days}
            inputMode="numeric"
            onChange={(event) => setDays(event.target.value)}
          />
        </label>
      </div>
    </Dialog>
  );
}

function BulkDialog({
  howMany,
  busy,
  onClose,
  onConfirm,
}: {
  readonly howMany: number;
  readonly busy: boolean;
  readonly onClose: () => void;
  readonly onConfirm: (days: number) => void;
}) {
  const [days, setDays] = useState("30");
  const parsed = wholeDays(days);
  return (
    <Dialog
      title={`Extend ${count(howMany)} passes`}
      warning={`This changes every pass on the page you are looking at, and only those. It writes ${count(howMany)} rows to the audit ledger, one per candidate. Narrow the filter first if that is not what you want.`}
      busy={busy || parsed === null}
      confirmLabel={`Extend ${count(howMany)} passes`}
      onClose={onClose}
      onConfirm={() => {
        if (parsed !== null) onConfirm(parsed);
      }}
    >
      <label className="block space-y-1">
        <span className={`${LABEL} block`}>Days</span>
        <input
          className={FIELD}
          value={days}
          inputMode="numeric"
          onChange={(event) => setDays(event.target.value)}
        />
      </label>
    </Dialog>
  );
}

